import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Auth token is now in HttpOnly cookie (sent automatically).
    // Also check localStorage for legacy token support.
    const legacyToken = localStorage.getItem('adminToken');
    if (legacyToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${legacyToken}`;
    }

    try {
      const response = await api.get('/admin/auth/me');
      setUser(response.data.user);
    } catch (err) {
      // Clear legacy token if auth fails
      localStorage.removeItem('adminToken');
      delete api.defaults.headers.common['Authorization'];
      // Only set error if it's not a simple "not logged in" case
      if (legacyToken) {
        setError(err.response?.data?.error || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, turnstileToken = null, totpCode = null) => {
    try {
      setError(null);
      const body = { email, password };
      if (turnstileToken) body.turnstileToken = turnstileToken;
      if (totpCode) body.totpCode = totpCode;
      const response = await api.post('/admin/auth/login', body);
      const { token, user } = response.data;

      // Password accepted, second factor still needed (200 with mfaRequired):
      // method 'totp' (authenticator app) or 'sms' (code texted to hint).
      if (response.data.mfaRequired && !user) {
        return {
          success: false,
          mfaRequired: true,
          method: response.data.method || 'totp',
          hint: response.data.hint || null,
          sent: response.data.sent !== false,
          error: response.data.sent === false ? response.data.error : null,
        };
      }
      if (!user) {
        const message = response.data.error || 'Login failed';
        setError(message);
        return { success: false, error: message };
      }

      // Legacy: store in localStorage for backward compat while cookie is also set
      if (token) {
        localStorage.setItem('adminToken', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      setUser(user);

      return { success: true };
    } catch (err) {
      if (err.response?.data?.mfaRequired) {
        return {
          success: false,
          mfaRequired: true,
          method: err.response.data.method || 'totp',
          hint: err.response.data.hint || null,
          sent: true,
          error: err.response.data.error || 'Code not accepted',
        };
      }
      const errorMessage = err.response?.data?.error || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await api.post('/admin/auth/logout');
    } catch (err) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('adminToken');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
    }
  };

  const setup = async (data) => {
    try {
      setError(null);
      const response = await api.post('/admin/auth/setup', data);
      const { token, user } = response.data;

      // Legacy: store in localStorage for backward compat while cookie is also set
      if (token) {
        localStorage.setItem('adminToken', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      setUser(user);

      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Setup failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await api.post('/admin/auth/change-password', { currentPassword, newPassword });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Failed to change password' };
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    setup,
    changePassword,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
